import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  MagnifyingGlass, ArrowClockwise, Download, Link as LinkIcon,
  CaretDown, CaretRight, Warning, Receipt,
} from '@phosphor-icons/react';
import { GeneratePaymentLinkDialog } from '../../components/admin/GeneratePaymentLinkDialog';
import { ReceiptDialog } from '../../components/admin/ReceiptDialog';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const JO_STATUSES = [
  { k: 'all', label: 'All Statuses' },
  { k: 'pending_procurement', label: 'Pending Procurement' },
  { k: 'procurement_approved', label: 'Procurement Approved' },
  { k: 'under_production', label: 'Under Production' },
  { k: 'production_completed', label: 'Production Completed' },
  { k: 'dispatched', label: 'Dispatched' },
  { k: 'pending_installation', label: 'Pending Installation' },
  { k: 'installation_completed', label: 'Installation Completed' },
];

const statusLabel = (k) => (JO_STATUSES.find((s) => s.k === k)?.label) || (k || '').replaceAll('_', ' ').toUpperCase();

function HealthBadge({ health, short_by }) {
  if (health === 'on_track') {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border border-green-300">On Track</Badge>;
  }
  return (
    <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border border-red-300">
      <Warning size={12} weight="bold" className="mr-1" />
      Short {fmtINR(short_by)}
    </Badge>
  );
}

function QuotationCard({ group, onSendLink, onOpenReceipt }) {
  const [open, setOpen] = useState(true);
  const partial = group.unconverted_balance > 0.005;

  return (
    <Card data-testid={`quotation-group-${group.quotation_number}`} className="border-zinc-300 rounded-sm">
      <CardContent className="p-0">
        {/* Parent row */}
        <div
          className="flex items-start justify-between gap-3 p-4 cursor-pointer hover:bg-zinc-50 border-b border-zinc-200"
          onClick={() => setOpen((o) => !o)}
        >
          <div className="flex items-start gap-2">
            {open ? <CaretDown size={16} className="mt-1 text-zinc-500" /> : <CaretRight size={16} className="mt-1 text-zinc-500" />}
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-bold text-zinc-950">Quotation {group.quotation_number}</h3>
                <Badge className="bg-zinc-200 text-zinc-800 hover:bg-zinc-200">{(group.quotation_status || '').toUpperCase()}</Badge>
                {group.has_actual_outstanding && (
                  <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border border-red-300">
                    Actual Outstanding {fmtINR(group.actual_outstanding)}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-zinc-600 mt-1">
                {group.customer_name} • {group.customer_mobile} • {group.work_location} • Executive: {group.executive_name}
              </div>
              <div className="text-xs text-zinc-500 mt-1">Date: {group.quotation_date}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Total Booked</div>
            <div className="text-xl font-bold text-zinc-950">{fmtINR(group.total_booked || group.quotation_total)}</div>
            <div className="text-xs text-zinc-500 mt-1">
              Received: <span className="text-green-700">{fmtINR(group.group_received)}</span> · Balance: <span className="text-red-700">{fmtINR(group.group_balance)}</span>
              {(group.group_excess || 0) > 0.005 && (
                <> · Excess: <span className="text-amber-700 font-semibold">{fmtINR(group.group_excess)}</span></>
              )}
            </div>
          </div>
        </div>

        {open && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-2 text-left">JO #</th>
                  <th className="p-2 text-left">Date</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-right">Qty</th>
                  <th className="p-2 text-right">SQFT</th>
                  <th className="p-2 text-left">Remarks</th>
                  <th className="p-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {group.job_orders.length === 0 ? (
                  <tr><td colSpan={7} className="p-4 text-center text-zinc-400">
                    No linked Job Orders yet
                  </td></tr>
                ) : group.job_orders.map((j) => (
                  <tr
                    key={j.jo_number}
                    className="border-b border-zinc-100 hover:bg-zinc-50"
                    data-testid={`jo-row-${j.jo_number}`}
                  >
                    <td className="p-2 font-mono text-zinc-800">{j.jo_number}</td>
                    <td className="p-2 text-zinc-700">{j.jo_date}</td>
                    <td className="p-2">
                      <Badge className={`text-[9px] ${j.is_installation_completed ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-blue-100 text-blue-800 hover:bg-blue-100'}`}>
                        {statusLabel(j.status)}
                      </Badge>
                    </td>
                    <td className="p-2 text-right text-zinc-800">{j.total_quantity || 0}</td>
                    <td className="p-2 text-right text-zinc-800">{Number(j.total_sqft || 0).toFixed(2)}</td>
                    <td className="p-2 text-zinc-600 max-w-[160px] truncate" title={j.remarks}>{j.remarks || '—'}</td>
                    <td className="p-2 text-center">
                      <div className="flex gap-1 justify-center">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => onOpenReceipt({
                            quotation_no: group.quotation_number,
                            jo_number: j.jo_number,
                            customer_name: group.customer_name,
                            customer_mobile: group.customer_mobile,
                            site_location: group.work_location,
                            executive_name: group.executive_name,
                          })}
                          data-testid={`receipt-btn-${j.jo_number}`}
                          title="Record Receipt"
                          className="text-emerald-700"
                        >
                          <Receipt size={12} />
                        </Button>
                        <Button
                          size="sm" variant="outline"
                          onClick={() => onSendLink({
                            customer_name: group.customer_name,
                            customer_mobile: group.customer_mobile,
                            amount: group.group_balance,
                            quotation_number: group.quotation_number,
                            jo_number: j.jo_number,
                            description: `Payment for JO ${j.jo_number}`,
                          })}
                          disabled={(group.group_balance || 0) <= 0.005}
                          data-testid={`send-link-btn-${j.jo_number}`}
                          title="Send Payment Link"
                        >
                          <LinkIcon size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {partial && (
                  <tr className="bg-amber-50 border-b border-amber-200">
                    <td className="p-2 font-mono text-amber-800 italic" colSpan={3}>Unconverted balance (not yet a Job Order)</td>
                    <td className="p-2 text-right text-amber-900 font-bold">{fmtINR(group.unconverted_balance)}</td>
                    <td className="p-2 text-right text-zinc-400">—</td>
                    <td className="p-2 text-right text-amber-900">{fmtINR(group.unconverted_balance)}</td>
                    <td className="p-2 text-right text-zinc-400" colSpan={2}>—</td>
                    <td className="p-2 text-center" colSpan={3}>
                      <span className="text-xs text-amber-700">Pending JO conversion</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OutstandingReport() {
  const [mainTab, setMainTab] = useState('report');   // "report" | "ledger"
  const [groups, setGroups] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('collection');   // "collection" | "actual"
  const [executives, setExecutives] = useState([]);
  const [filters, setFilters] = useState({
    executive_name: '', date_from: '', date_to: '',
    customer_mobile: '', jo_status: 'all', payment_health: 'all',
  });
  const [showPL, setShowPL] = useState(false);
  const [plPrefill, setPlPrefill] = useState({});
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptCtx, setReceiptCtx] = useState({});

  useEffect(() => {
    api.get('/api/masters/executives').then(r => setExecutives(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, view_mode: viewMode };
      Object.keys(params).forEach((k) => { if (params[k] === '' || params[k] === null || params[k] === 'all') delete params[k]; });
      params.view_mode = viewMode;   // always include view_mode
      const { data } = await api.get('/api/admin/reports/outstanding-v2', { params });
      setGroups(data.groups || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters, viewMode]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    Object.entries({ ...filters, view_mode: viewMode }).forEach(([k, v]) => {
      if (v && v !== 'all') params.append(k, v);
    });
    params.set('view_mode', viewMode);
    window.open(`/api/admin/reports/outstanding-v2/export?${params.toString()}`, '_blank');
  };

  const onSendLink = (prefill) => {
    setPlPrefill(prefill);
    setShowPL(true);
  };

  const onOpenReceipt = (ctx) => {
    setReceiptCtx(ctx);
    setReceiptOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="outstanding-report-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-light tracking-tight text-zinc-950">Outstanding Report</h1>
            <p className="text-sm text-zinc-500 mt-1">Quotation-wise payment & collection tracking.</p>
          </div>
          {mainTab === 'report' && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={load} data-testid="reload-btn"><ArrowClockwise size={16} className="mr-1" /> Reload</Button>
              <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn"><Download size={16} className="mr-1" /> Export CSV</Button>
            </div>
          )}
        </div>

        {/* Top-level tabs */}
        <div className="flex gap-2 border-b border-zinc-300">
          {[
            { k: 'report', label: 'Outstanding Report' },
            { k: 'ledger', label: 'Customer Ledger' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setMainTab(t.k)}
              data-testid={`main-tab-${t.k}`}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-[0.15em] border-b-2 transition-colors ${
                mainTab === t.k ? 'border-orange-500 text-orange-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
              }`}
            >{t.label}</button>
          ))}
        </div>

        {mainTab === 'ledger' ? <CustomerLedgerTab /> : (
          <>
        {/* View mode toggle */}
        <div className="flex gap-2 border-b border-zinc-200">
          {[
            { k: 'collection', label: 'Collection Tracking', desc: 'All converted quotations & payment stage' },
            { k: 'actual', label: 'Actual Outstanding', desc: 'Only post-installation JOs with balance' },
          ].map((v) => (
            <button
              key={v.k}
              onClick={() => setViewMode(v.k)}
              data-testid={`view-mode-${v.k}`}
              className={`px-4 py-2 text-sm border-b-2 ${
                viewMode === v.k
                  ? 'border-orange-500 text-orange-600 font-bold'
                  : 'border-transparent text-zinc-500 hover:text-zinc-800'
              }`}
              title={v.desc}
            >{v.label}</button>
          ))}
        </div>

        {/* Filters */}
        <Card className="border-zinc-300 rounded-sm"><CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Executive</label>
              <Select value={filters.executive_name || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, executive_name: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="filter-executive"><SelectValue placeholder="All Executives" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Executives</SelectItem>
                  {executives.map((e) => <SelectItem key={e.name} value={e.name}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">JO Status</label>
              <Select value={filters.jo_status} onValueChange={(v) => setFilters((f) => ({ ...f, jo_status: v }))}>
                <SelectTrigger data-testid="filter-jo-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {JO_STATUSES.map((s) => <SelectItem key={s.k} value={s.k}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Payment Health</label>
              <Select value={filters.payment_health} onValueChange={(v) => setFilters((f) => ({ ...f, payment_health: v }))}>
                <SelectTrigger data-testid="filter-health"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="on_track">On Track</SelectItem>
                  <SelectItem value="short">Short</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Customer Mobile</label>
              <Input value={filters.customer_mobile} onChange={(e) => setFilters((f) => ({ ...f, customer_mobile: e.target.value }))}
                     data-testid="filter-mobile" placeholder="9008..." />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Quotation Date From</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                     data-testid="filter-date-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-zinc-500">Quotation Date To</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                     data-testid="filter-date-to" />
            </div>
          </div>
        </CardContent></Card>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: 'Quotations', v: totals.quotation_count || 0, tone: 'text-zinc-950' },
            { label: 'Linked JOs', v: totals.jo_count || 0, tone: 'text-zinc-950' },
            { label: 'Total Booked', v: fmtINR(totals.total_booked || totals.converted_value), tone: 'text-zinc-950' },
            { label: 'Total Received', v: fmtINR(totals.group_received), tone: 'text-green-700' },
            { label: viewMode === 'actual' ? 'Actual Outstanding' : 'Balance to Collect', v: fmtINR(viewMode === 'actual' ? totals.actual_outstanding : totals.group_balance), tone: 'text-red-700' },
            { label: 'Excess Received', v: fmtINR(totals.group_excess || 0), tone: 'text-amber-700' },
          ].map((t) => (
            <Card key={t.label} className="border-zinc-300 rounded-sm"><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t.label}</div>
              <div className={`text-lg font-bold ${t.tone}`}>{t.v}</div>
            </CardContent></Card>
          ))}
        </div>

        {/* Groups */}
        {loading ? (
          <div className="text-center text-zinc-400 py-10">Loading…</div>
        ) : groups.length === 0 ? (
          <Card className="border-zinc-300 rounded-sm"><CardContent className="p-10 text-center text-zinc-500">
            <MagnifyingGlass size={32} className="mx-auto mb-2 text-zinc-300" />
            No quotations match the current filters
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <QuotationCard key={g.quotation_number} group={g} onSendLink={onSendLink} onOpenReceipt={onOpenReceipt} />
            ))}
          </div>
        )}
          </>
        )}
      </div>

      <GeneratePaymentLinkDialog
        open={showPL}
        onOpenChange={setShowPL}
        prefill={plPrefill}
      />
      <ReceiptDialog
        open={receiptOpen}
        onOpenChange={setReceiptOpen}
        quotationNo={receiptCtx.quotation_no}
        joNumber={receiptCtx.jo_number}
        prefill={{
          customer_name: receiptCtx.customer_name,
          customer_mobile: receiptCtx.customer_mobile,
          site_location: receiptCtx.site_location,
          executive_name: receiptCtx.executive_name,
        }}
        onCreated={() => load()}
      />
    </Layout>
  );
}

// ==================== CUSTOMER LEDGER TAB ====================

function CustomerLedgerTab() {
  const [mobile, setMobile] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const m = mobile.replace(/\D/g, '').slice(-10);
    if (m.length !== 10) { toast.error('Enter a valid 10-digit mobile'); return; }
    setLoading(true);
    setLedger(null);
    try {
      const { data } = await api.get(`/api/admin/customer-ledger/${m}`);
      setLedger(data);
      if (data.totals.total_quotations === 0) {
        toast.info('No quotations found for this customer.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ledger');
    } finally { setLoading(false); }
  };

  const downloadPdf = () => {
    if (!ledger) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/customer-ledger/${ledger.customer.mobile}/pdf`, '_blank');
  };

  const exportCsv = () => {
    if (!ledger) return;
    const rows = [];
    rows.push(['Statement of Account']);
    rows.push(['Customer', ledger.customer.name || '', 'Mobile', ledger.customer.mobile || '']);
    rows.push(['Executive', ledger.customer.assigned_executive_name || '', 'Site', ledger.customer.site_location || '']);
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Total Quotations', ledger.totals.total_quotations]);
    rows.push(['Total Order Value', ledger.totals.total_order_value]);
    rows.push(['Total Received', ledger.totals.total_received]);
    rows.push(['Balance', ledger.totals.total_balance]);
    rows.push(['Unallocated Advance', ledger.totals.advance_unallocated]);
    rows.push([]);
    rows.push(['Quotations']);
    rows.push(['Quotation No', 'Date', 'Status', 'Site', 'Order Value', 'Received', 'Balance', 'Linked JOs']);
    ledger.quotations.forEach((q) => rows.push([
      q.quotation_no, q.quotation_date || '', q.status || '', q.site_location || '',
      q.order_value, q.total_received, q.balance,
      (q.job_orders || []).map((j) => j.jo_number).join('|'),
    ]));
    rows.push([]);
    rows.push(['Receipts']);
    rows.push(['Receipt No', 'Date', 'Mode', 'Bank', 'Reference', 'Quotation', 'JO', 'Source', 'Amount']);
    ledger.receipts.forEach((r) => rows.push([
      r.receipt_number || '', r.payment_date || '', r.payment_mode || r.mode || '',
      r.received_bank || '', r.reference || '', r.quotation_no || '', r.jo_number || '',
      r.payment_source || r.source || '', r.amount,
    ]));
    const csv = rows.map((r) => r.map((c) => {
      const s = String(c ?? '');
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Statement_${(ledger.customer.name || ledger.customer.mobile).replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendWa = () => {
    if (!ledger) return;
    const c = ledger.customer;
    const t = ledger.totals;
    const lines = [];
    lines.push(`*Statement of Account*`);
    lines.push(`Customer: ${c.name}`);
    lines.push(`Mobile: ${c.mobile}`);
    lines.push(``);
    lines.push(`Total Quotations: ${t.total_quotations}`);
    lines.push(`Total Order Value: ₹${Number(t.total_order_value).toLocaleString('en-IN')}`);
    lines.push(`Total Received: ₹${Number(t.total_received).toLocaleString('en-IN')}`);
    lines.push(`*Balance Receivable: ₹${Number(t.total_balance).toLocaleString('en-IN')}*`);
    if (t.advance_unallocated > 0) {
      lines.push(`Unallocated Advance: ₹${Number(t.advance_unallocated).toLocaleString('en-IN')}`);
    }
    lines.push(``);
    lines.push(`— Karthik Doors & Windows`);
    const text = encodeURIComponent(lines.join('\n'));
    window.open(`https://wa.me/91${c.mobile}?text=${text}`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="customer-ledger-tab">
      <Card className="border-zinc-300 rounded-sm"><CardContent className="p-4">
        <div className="flex flex-col md:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Customer Mobile Number</label>
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="10-digit mobile — pulls the customer's complete statement"
              data-testid="ledger-mobile-input"
              onKeyDown={(e) => e.key === 'Enter' && search()}
            />
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={search} disabled={loading} data-testid="ledger-search-btn">
            {loading ? <ArrowClockwise size={16} className="animate-spin" /> : <MagnifyingGlass size={16} className="mr-1" />}
            {loading ? 'Loading…' : 'Fetch Statement'}
          </Button>
        </div>
      </CardContent></Card>

      {ledger && (
        <>
          <Card className="border-zinc-300 rounded-sm"><CardContent className="p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-2xl font-bold text-zinc-900">{ledger.customer.name || 'Unnamed Customer'}</div>
                <div className="text-sm text-zinc-500 font-mono">{ledger.customer.mobile}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Executive: <span className="font-bold">{ledger.customer.assigned_executive_name || '—'}</span>
                  {ledger.customer.site_location && <> · Site: {ledger.customer.site_location}</>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportCsv} data-testid="ledger-csv-btn"><Download size={14} className="mr-1" /> Excel/CSV</Button>
                <Button variant="outline" onClick={downloadPdf} data-testid="ledger-pdf-btn"><Download size={14} className="mr-1" /> PDF</Button>
                <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={sendWa} data-testid="ledger-wa-btn">
                  <Download size={14} className="mr-1 rotate-180" /> WhatsApp
                </Button>
              </div>
            </div>
          </CardContent></Card>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <LedgerMetric label="Total Quotations" value={ledger.totals.total_quotations} isCount testid="ledger-count" />
            <LedgerMetric label="Total Order Value" value={ledger.totals.total_order_value} accent="text-zinc-900" testid="ledger-order-value" />
            <LedgerMetric label="Total Received" value={ledger.totals.total_received} accent="text-blue-700" testid="ledger-received" />
            <LedgerMetric label="Balance Receivable" value={ledger.totals.total_balance} accent="text-red-700" testid="ledger-balance" />
            <LedgerMetric label="Unallocated Advance" value={ledger.totals.advance_unallocated} accent="text-emerald-700" />
          </div>

          <Card className="border-zinc-300 rounded-sm"><CardContent className="p-0 overflow-x-auto">
            <div className="p-3 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-100">Quotations ({ledger.quotations.length})</div>
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-3 text-left">Quotation No</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Status</th>
                  <th className="p-3 text-left">Site</th>
                  <th className="p-3 text-left">Linked JO(s)</th>
                  <th className="p-3 text-right">Order Value</th>
                  <th className="p-3 text-right">Received</th>
                  <th className="p-3 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.quotations.length === 0 ? (
                  <tr><td colSpan={8} className="p-6 text-center text-zinc-400">No quotations for this customer.</td></tr>
                ) : ledger.quotations.map((q, qi) => (
                  <tr key={q.quotation_no || qi} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="p-3 font-mono text-xs font-bold text-orange-700">{q.quotation_no}</td>
                    <td className="p-3 text-xs">{(q.quotation_date || '').slice(0, 10)}</td>
                    <td className="p-3 text-xs uppercase">{q.status || '—'}</td>
                    <td className="p-3 text-xs">{q.site_location || '—'}</td>
                    <td className="p-3 text-xs font-mono">
                      {(q.job_orders || []).length === 0 ? '—' : q.job_orders.map((j) => (
                        <div key={j.jo_number}>{j.jo_number} <span className="text-[10px] text-zinc-400">({j.status})</span></div>
                      ))}
                    </td>
                    <td className="p-3 text-right font-bold">{'₹ ' + Number(q.order_value).toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right text-blue-700 font-bold">{'₹ ' + Number(q.total_received).toLocaleString('en-IN')}</td>
                    <td className="p-3 text-right text-red-700 font-bold">{'₹ ' + Number(q.balance).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>

          <Card className="border-zinc-300 rounded-sm"><CardContent className="p-0 overflow-x-auto">
            <div className="p-3 text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-100">Receipts ({ledger.receipts.length})</div>
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-3 text-left">Receipt No</th>
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-left">Bank / Reference</th>
                  <th className="p-3 text-left">Quotation / JO</th>
                  <th className="p-3 text-center">Source</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {ledger.receipts.length === 0 ? (
                  <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No receipts recorded.</td></tr>
                ) : ledger.receipts.map((r, i) => (
                  <tr key={r._id || r.receipt_number || i} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="p-3 font-mono text-xs font-bold text-orange-700">{r.receipt_number || '—'}</td>
                    <td className="p-3 text-xs">{r.payment_date || '—'}</td>
                    <td className="p-3 text-xs">{r.payment_mode || r.mode || '—'}</td>
                    <td className="p-3 text-xs">
                      <div>{r.received_bank || '—'}</div>
                      <div className="text-[10px] text-zinc-500 font-mono">{r.reference || ''}</div>
                    </td>
                    <td className="p-3 text-xs font-mono">
                      {r.quotation_no || '—'}
                      {r.jo_number && <div className="text-[10px] text-zinc-500">{r.jo_number}</div>}
                    </td>
                    <td className="p-3 text-center text-[10px]">{(r.payment_source || r.source || '').toUpperCase()}</td>
                    <td className="p-3 text-right text-emerald-700 font-bold">{'₹ ' + Number(r.amount).toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        </>
      )}
    </div>
  );
}

function LedgerMetric({ label, value, accent, isCount, testid }) {
  return (
    <Card className="border-zinc-300 rounded-sm"><CardContent className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-lg font-bold mt-1 ${accent || 'text-zinc-800'}`} data-testid={testid}>
        {isCount ? Number(value || 0).toLocaleString('en-IN') : '₹ ' + Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
      </div>
    </CardContent></Card>
  );
}

export default OutstandingReport;
export { OutstandingReport };
